import { Area } from 'react-easy-crop';

export const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous'); // needed to avoid cross-origin issues on CodeSandbox
    image.src = `${url}?query=${Date.now()}`;
  });

export function getRadianAngle(degreeValue: number) {
  return (degreeValue * Math.PI) / 180;
}

/**
 * Returns the new bounding area of a rotated rectangle.
 */
export function rotateSize(width: number, height: number, rotation: number) {
  const rotRad = getRadianAngle(rotation);

  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

export async function getCroppedImageFile(
  imageSrc: string,
  crop: Area,
  rotation = 0,
  mime = 'image/png'     // keep PNG by default, or pass 'image/jpeg'
): Promise<File | null> {
  const image = await new Promise<HTMLImageElement>((res, rej) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // keeps CORS happy for remote sources
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = `${imageSrc}?t=${Date.now()}`;
  });

  const dpr = window.devicePixelRatio || 1;
  const canvas = document.createElement('canvas');
  canvas.width = crop.width * dpr;
  canvas.height = crop.height * dpr;

  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // prevent browser from blurring while scaling the image up to DPR size
  ctx.imageSmoothingEnabled = false;

  ctx.save();
  ctx.scale(dpr, dpr); // draw in CSS pixels, scaled up
  if (rotation) {
    // move origin to centre, rotate, move back
    ctx.translate(crop.width / 2, crop.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-crop.width / 2, -crop.height / 2);
  }
  ctx.drawImage(
    image,
    crop.x, // top‑left of source rectangle (original pixels)
    crop.y,
    crop.width,
    crop.height,
    0, 0, // draw to (0,0) of the canvas
    crop.width,
    crop.height
  );
  ctx.restore();

  return new Promise<File | null>((resolve) =>
    canvas.toBlob(
      (blob) =>
        resolve(
          blob
            ? new File([blob], `crop.${mime === 'image/png' ? 'png' : 'jpg'}`, { type: mime })
            : null
        ),
      mime,
      mime.startsWith('image/jpeg') ? 1 : undefined // <- quality = 1 for JPEG
    )
  );
}

// ---------------------------------------------------------------------------
// Capped square crop (JT1-1539)
//
// Powers the avatar/logo camera affordance. Unlike getCroppedImageFile above (which appends `?t=` to
// the src and so only works with remote http(s) URLs), this reads the passed source verbatim — it is
// given a local `blob:`/`data:` URL so the crop happens client-side with no round-trip. The output is
// always a square and is compressed to stay at/under ~1MB (JPEG quality step-down, PNG dimension
// step-down), matching the ticket's "client-side crop to square, ~1MB cap, JPG/PNG" rule.
// ---------------------------------------------------------------------------

/** ~1MB. */
export const DEFAULT_IMAGE_MAX_BYTES = 1024 * 1024;
/** Avatars/logos never render larger than this, so there is no point storing more pixels. */
export const DEFAULT_IMAGE_MAX_DIMENSION = 512;

export interface CroppedImageOptions {
  /** Hard ceiling on the encoded file size in bytes. Default ~1MB. */
  maxBytes?: number;
  /** Longest side of the output square in pixels. Default 512. */
  maxDimension?: number;
  /** Output encoding. JPEG for photos; PNG to preserve logo transparency. Default JPEG. */
  format?: 'jpeg' | 'png';
}

const loadImageElement = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src; // verbatim — do NOT append a query string (would corrupt blob:/data: URLs)
  });

const toBlobAsync = (canvas: HTMLCanvasElement, mime: string, quality?: number): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), mime, quality));

const scaleCanvas = (source: HTMLCanvasElement, factor: number): HTMLCanvasElement => {
  const next = document.createElement('canvas');
  next.width = Math.max(1, Math.round(source.width * factor));
  next.height = Math.max(1, Math.round(source.height * factor));
  next.getContext('2d')?.drawImage(source, 0, 0, next.width, next.height);
  return next;
};

/** JPEG steps quality down; PNG (no quality channel) steps the dimensions down. */
async function encodeWithinCap(
  canvas: HTMLCanvasElement,
  mime: string,
  maxBytes: number
): Promise<Blob | null> {
  if (mime === 'image/jpeg') {
    const qualities = [0.9, 0.82, 0.72, 0.6, 0.5];
    let last: Blob | null = null;
    for (let i = 0; i < qualities.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      const blob = await toBlobAsync(canvas, mime, qualities[i]);
      last = blob ?? last;
      if (blob && blob.size <= maxBytes) return blob;
    }
    return last;
  }

  let current = canvas;
  for (let i = 0; i < 4; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    const blob = await toBlobAsync(current, mime);
    if (blob && (blob.size <= maxBytes || current.width <= 128)) return blob;
    current = scaleCanvas(current, 0.8);
  }
  return toBlobAsync(current, mime);
}

/**
 * Crops `crop` (in source-image pixels, as react-easy-crop reports) out of `imageSrc`, applies
 * `rotation`, and returns a square File encoded within the size cap. Returns null if the canvas is
 * unavailable (e.g. under a test DOM) or encoding fails.
 */
export async function getCroppedSquareFile(
  imageSrc: string,
  crop: Area,
  rotation = 0,
  options: CroppedImageOptions = {}
): Promise<File | null> {
  const {
    maxBytes = DEFAULT_IMAGE_MAX_BYTES,
    maxDimension = DEFAULT_IMAGE_MAX_DIMENSION,
    format = 'jpeg',
  } = options;
  const mime = format === 'png' ? 'image/png' : 'image/jpeg';

  const image = await loadImageElement(imageSrc);

  // Draw the (optionally rotated) image onto a canvas sized to its rotated bounding box, then read
  // the crop rectangle back out — the standard react-easy-crop rotation-safe recipe.
  const rotRad = getRadianAngle(rotation);
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotation);

  const rotated = document.createElement('canvas');
  rotated.width = Math.max(1, Math.round(bBoxWidth));
  rotated.height = Math.max(1, Math.round(bBoxHeight));
  const rctx = rotated.getContext('2d');
  if (!rctx) return null;
  rctx.translate(rotated.width / 2, rotated.height / 2);
  rctx.rotate(rotRad);
  rctx.translate(-image.width / 2, -image.height / 2);
  rctx.drawImage(image, 0, 0);

  const cropW = Math.max(1, Math.round(crop.width));
  const cropH = Math.max(1, Math.round(crop.height));
  const cropData = rctx.getImageData(Math.round(crop.x), Math.round(crop.y), cropW, cropH);

  const cropCanvas = document.createElement('canvas');
  cropCanvas.width = cropW;
  cropCanvas.height = cropH;
  const cctx = cropCanvas.getContext('2d');
  if (!cctx) return null;
  cctx.putImageData(cropData, 0, 0);

  // Force a square and cap the dimension.
  const outSize = Math.max(1, Math.min(cropW, maxDimension));
  const out = document.createElement('canvas');
  out.width = outSize;
  out.height = outSize;
  const octx = out.getContext('2d');
  if (!octx) return null;
  octx.drawImage(cropCanvas, 0, 0, cropW, cropH, 0, 0, outSize, outSize);

  const blob = await encodeWithinCap(out, mime, maxBytes);
  if (!blob) return null;

  const ext = mime === 'image/png' ? 'png' : 'jpg';
  return new File([blob], `crop.${ext}`, { type: blob.type || mime });
}